// 局部替换
import { ModuleEnum } from "../moduleEnum";
import type { LayoutReferState } from "@/types/image";
import { getModelEventTrackType } from "../index";
// import { $bus } from '@/mitt';
import { useArtworkStore } from "@/store/artwork";
import { useUserStore } from "@/store/user";
import { filteringInvalidFields } from "@/utils";
import { useEventTracking } from "@/hooks/useEventTracking";
import type { ModelState } from "@/types/model";
import { startModuleGenerate } from "./index";
import { useModelStore } from "@/store/model";
import { styleModelOptions } from "@/constants";
import { getModelDetail } from "holopix-api/src/server/model";
import { getModuleLabelById } from "@xmiles/holopix-helper";
import { IS_EXPANSION_V2_ENABLED } from '@/constants'

export const module = ModuleEnum["expansion"];

// 专用模型 id（旧工作流 lora）
export const expansionModelId = ["production", "gray"].includes(import.meta.env.VITE_ENV) ? 2131 : 679;

import { VISUAL_EXPANSION_V2_MODEL_ID } from "../moduleEnum";

export { VISUAL_EXPANSION_V2_MODEL_ID };

export interface ExpansionPadParams {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

// 生图参数
export const expansionParams = {
  module,
  prompt: "",
  negativePrompt: "",
  // batchSize: defaultBatchSize,
  batchSize: 1,
  layoutRefer: null as null | LayoutReferState, // 原始稿件
  maskImage: "",
  aspectRatios: "0:0", // 尺寸比例
  maskContrastImage: null as LayoutReferState | null,
  styleReferList: [] as LayoutReferState[],
  originStyleRefer: null as null | LayoutReferState,
  showOriginImage: false, // 查看原图
  padParams: null as ExpansionPadParams | null,
};

// 将当前用户输入的生图参数转换成后端所需要
async function getExpansionGenerateParam() {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  const module = expansionParams.module;
  const moduleParams = artworkStore.expansion.params;

  /***
   *
   * 由于历史原因问题,只要是没有模型的模块就会出现问题,取值是错误的,但是最终上传到服务端的数据是正确的,因为是写死的,
   * 所以只能在这里先调用一次接口把数据先纠正回来,到时候再说
   */

  let data = await getModelDetail({ id: expansionModelId });

  const isWorkFlowV2 = IS_EXPANSION_V2_ENABLED;
  // 旧工作流：画布导出的灰底拼接图作为 layoutRefer；V2 仅传用户原图，扩区位置由 padParams 描述
  const layoutReferSource = isWorkFlowV2
    ? moduleParams.layoutRefer
    : moduleParams._referenceImage?.[0] ?? moduleParams.layoutRefer;

  // 返回结果第一个是原图，第二个是蒙层图片
  console.log(moduleParams._referenceImage, "moduleParams._referenceImage");
  let params = {
    module,
    modelList: [data],
    loraList: [{ modelId: expansionModelId }],
    source: 1,
    userId: userStore.userInfo.id,
    uniqueId: userStore.userInfo.uniqueId,
    artworkId: artworkStore.artworkState.id,
    // layoutRefer: moduleParams.layoutRefer,
    layoutRefer: layoutReferSource,
    layoutReferId: layoutReferSource?.id || null,
    maskImage: moduleParams.maskContrastImage,
    aspectRatios: moduleParams.aspectRatios,
    imageGuidanceWeights: 40, // 图片引导权重,固定值
    imgTags: moduleParams.imgTags,
    historyJsonUrl: moduleParams.historyJsonUrl,
    originLayoutRefer: {
      ...(moduleParams.originLayoutRefer ?? layoutReferSource ?? moduleParams.layoutRefer),
    },
    _referenceImage: isWorkFlowV2
      ? [moduleParams.layoutRefer, moduleParams.maskContrastImage]
      : moduleParams._referenceImage
  } as any;

  // 如果 maskImage 存在，则将 layoutRefer 的宽度设置为 maskImage 的宽度（V2 保持用户原图尺寸，由 padParams 对齐画布）
  if (params.maskImage && moduleParams.aspectRatios === "0:0" && !isWorkFlowV2) {
    params.layoutRefer.width = params.maskImage.width;
    params.layoutRefer.height = params.maskImage.height;
  }

  params.maskId = moduleParams.maskContrastImage?.id || null;
  params.batchSize = moduleParams.batchSize;
  params.prompt = moduleParams.prompt;
  params.imgTags = moduleParams.imgTags;

  params.styleReferList = [];
  params.commonReferId = null;

  // 去除 layoutRefer 中的无效字段
  filteringInvalidFields(params.layoutRefer, ["id", "width", "height", "image"]);

  // 是否使用加速通道
  params.vipChannel = artworkStore.channel === "expedite";

  // 全量使用V2版本工作流
  if (IS_EXPANSION_V2_ENABLED) {
    params.module = VISUAL_EXPANSION_V2_MODEL_ID;
    params.loraList = [];
    if (moduleParams.padParams) {
      params.padParams = moduleParams.padParams;
    }
  }

  //将一些东西塞进artworkStore进去先
  artworkStore.expansion.params.modelList = [data];

  return params;
}

// 开启模块生图
export async function startExpansionGenerate(
  oldParams?: any,
  options?: {
    onClientIdGenerated?: (clientId: string) => void;
    onClientIdCleared?: () => void;
  }
) {
  const artworkStore = useArtworkStore();
  const modelStore = useModelStore();
  const params = await getExpansionGenerateParam();
  const module = expansionParams.module;
  const modelList: any[] = params.modelList || [];

  const modelLabels = modelList.flatMap((item) => item.label || []);
  // 按照 modelLabels 中 ID 的顺序过滤和排序标签
  const modelTagList = modelLabels
    .map((labelId) => modelStore.labelOptions.find((item: any) => item.value === labelId))
    .filter(Boolean)
    .map((item) => item!.label);

  const modelCategories = modelList.map((item) => item.category).filter(Boolean);

  const modelTypeList = modelCategories
    .map((categoryId) => modelStore.categoryOptions.find((item: any) => item.value === categoryId))
    .filter(Boolean)
    .map((item) => item!.label);

  const isUserGeneratedText = (() => {
    if (modelList.every((i) => i.userId !== 0)) return "是";
    if (modelList.every((i) => i.userId === 0)) return "否";
    return "混合";
  })();
  const modelIdList = params.modelList.map((item: ModelState) => item.id);
  const modelNameList = modelList.map((i: any) => i.modelName) || [];
  // 创建loraType映射关系
  const loraTypeMap = Object.fromEntries(styleModelOptions.map((option) => [option.value, option.label]));
  // 提取loraType对应的label组成数组
  const loraTypeLabels = modelList.map((item) => (item.loraType ? loraTypeMap[item.loraType] || "视频模型" : item.loraType));
  startModuleGenerate(module, {
    getModuleParams: () => {
      return oldParams || params;
    },
    onStartTrack: (clientId) => {
      reportStartEventTracking(clientId);
    },
    onFinishTrack: (data) => {
      reportFinishEventTracking(data);
    },
    onClientIdGenerated: options?.onClientIdGenerated,
    onClientIdCleared: options?.onClientIdCleared
  });

  // 提交生图埋点
  function reportStartEventTracking(clientId: string) {
    const eventValue: any = {
      client_id: clientId,
      module: getModuleLabelById(module) as string,
      source: params.source === 1 ? "直接生成" : "再迭代",
      artwork_id: artworkStore.artworkState.id.toString(),
      prompt: params.prompt,
      negative_prompt: params.negativePrompt,
      face_detail: params.faceDetail ? 1 : 0,
      simplebg: params.simpleBackground ? 1 : 0,
      style_refer: params.styleReferList?.length > 0 ? "1" : "0",
      batchSize: params.batchSize,
      // model_id: params.modelList.map((item: ModelState) => item.id).join(','),
      hp_model_id: String(modelIdList.join(",")),
      model_name: params.modelList.map((item: ModelState) => item.modelName).join(",") || JSON.stringify(params.modelList || ["--"]),
      ckpt_type: params.modelList.map((item: ModelState) => item.ckptType).join(","),
      lora_type: params.modelList.map((item: ModelState) => item.loraType).join(","),
      is_personal_model: getModelEventTrackType(params.modelList),
      is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
      model_tags: modelTagList.join(","),
      model_type: modelTypeList.join(","),

      hp_model_id_list: modelIdList,
      hp_model_name_list: modelNameList,
      hp_model_tags_list: modelTagList,
      hp_model_type_list: modelTypeList,
      hp_is_user_generated: isUserGeneratedText,
      is_composite_model: loraTypeLabels
    };

    useEventTracking({
      event: "creation_event",
      eventValue
    });
  }

  function reportFinishEventTracking({ client_id = "", result = 0 as 0 | 1, imageIds = "", queue_time_number = 0, loading_time_number = 0 } = {}) {
    useEventTracking({
      event: "artwork_status_event",
      eventValue: {
        module: getModuleLabelById(module) as string,
        client_id,
        source: params.source === 1 ? "直接生成" : "再迭代",
        img_id: imageIds,
        result,
        queue_time_number,
        loading_time_number,
        // model_id: params.modelList.map((item: ModelState) => item.id).join(','),
        hp_model_id: String(modelIdList.join(",")),
        hp_model_id_list: modelIdList,
        model_name: params.modelList.map((item: ModelState) => item.modelName).join(",") || JSON.stringify(params.modelList || ["--"]),
        ckpt_type: params.modelList.map((item: ModelState) => item.ckptType).join(","),
        lora_type: params.modelList.map((item: ModelState) => item.loraType).join(","),
        is_personal_model: getModelEventTrackType(params.modelList),
        is_team_share_works: artworkStore.artworkState.shareType === 1 ? 1 : 0,
        prompt: params.prompt,
        negative_prompt: params.negativePrompt,
        face_detail: params.faceDetail ? 1 : 0,
        simplebg: params.simpleBackground ? 1 : 0,
        style_refer: params.styleReferList?.length > 0 ? "1" : "0",
        model_tags: modelTagList.join(","),
        hp_model_tags_list: modelTagList,
        model_type: modelTypeList.join(","),
        hp_model_type_list: modelTypeList,
        hp_is_user_generated: isUserGeneratedText
      }
    });
  }
}
